import api, { resumeQueue } from '@/app/api/axios.js';
import { dehydrate, hydrate } from '@/app/services/hydrate.js';
import { isNavigationFailure } from 'vue-router';
import router from '@/app/router/index.js';
import { queryClient } from '@/plugins/vue-query.js';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';
import { authApi } from '#modules/auth/api/auth.api.js';

const TOKEN_REFRESH_BUFFER_SECONDS = 60; // Refresh this many seconds before expiry
const TOKEN_FRESHNESS_THRESHOLD_SECONDS = 10; // Skip refresh if token is fresher than this
const MIN_REFRESH_DELAY_MS = 30_000; // Minimum delay between refreshes to prevent tight loops

/**
 * Computes a safe refresh delay: at least MIN_REFRESH_DELAY_MS, but never past token expiry.
 *
 * @param {number} expiresIn - Token lifetime in seconds
 * @returns {number} Delay in milliseconds
 */
function computeRefreshDelay(expiresIn) {
    const target = (expiresIn - TOKEN_REFRESH_BUFFER_SECONDS) * 1000;
    const upperBound = Math.max((expiresIn - TOKEN_FRESHNESS_THRESHOLD_SECONDS) * 1000, 0);
    const clamped = Math.min(Math.max(target, MIN_REFRESH_DELAY_MS), upperBound);

    return Math.max(clamped, MIN_REFRESH_DELAY_MS);
}

let refreshTimeout;

/**
 * Authenticates user and sets up automatic token refresh
 *
 * @param {object} data - Authentication response data
 * @param {string} data.access_token - The access token
 * @param {string} data.refresh_token - The refresh token
 * @param {number} data.expires_in - Token expiry in seconds
 * @returns {Promise<void>}
 * @throws {Error} If hydration fails
 */
export async function authenticate(data) {
    const authStore = useAuthStore();

    // Refresh the token before the access token expires. This means the user will stay
    // logged in without any noticeable hiccups or delays
    refreshTimeout = setTimeout(() => refresh(), computeRefreshDelay(data.expires_in));

    const accessToken = data.access_token;

    // Add the header to the API handler for every request
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    authStore.accessToken = accessToken;
    authStore.refreshToken = data.refresh_token;
    authStore.accessTokenExpiry = Date.now() + data.expires_in * 1000;

    try {
        await hydrate();
    } catch (error) {
        // If hydration fails, clean up auth state
        console.error('Failed to hydrate user data after authentication:', error);
        await logout({ navigate: false, reason: LogoutReason.AUTO });
        throw error; // Re-throw so caller knows authentication failed
    }
}

export const LogoutReason = {
    SESSION_EXPIRED: 'session_expired',
    SIGN_OUT: 'sign_out',
    AUTO: 'auto',
};

let isRefreshing = false;
let refreshPromise = null;
/**
 * Flag to allow refresh during initial app load even without accessToken in memory.
 * This handles the case where tokens exist in localStorage but not yet loaded into store.
 * After first refresh attempt, we require accessToken to be present.
 */
let firstRefresh = true;

/**
 * Refreshes the access token using the refresh token
 *
 * @param {object} [options] - Refresh options
 * @param {boolean} [options.navigate=true] - Whether to navigate to login on failure
 * @param {boolean} [options.force=false] - Bypass the freshness check (use when the server has already rejected the token)
 * @returns {Promise<string|undefined>} Returns authorization header on success, undefined if skipped
 * @throws {Error} If refresh fails (after triggering logout)
 */
export async function refresh({ navigate, force } = { navigate: true, force: false }) {
    const authStore = useAuthStore();

    // Allow refresh during initial page load
    if (firstRefresh) {
        firstRefresh = false;
    } else if (!authStore.accessToken) {
        return; // Skip if no access token is available
    }

    // Return the in-flight promise so concurrent callers share the same result
    if (isRefreshing) {
        return refreshPromise;
    }

    // Skip refresh if no refresh token is available
    if (!authStore.refreshToken) {
        return;
    }

    // Skip refresh if access token is still fresh — bypassed when the server has already
    // rejected the token (401), since client-side expiry can be wrong due to revocation or clock skew
    if (
        !force &&
        authStore.accessTokenExpiry &&
        Date.now() < authStore.accessTokenExpiry - TOKEN_FRESHNESS_THRESHOLD_SECONDS * 1000
    ) {
        return;
    }

    isRefreshing = true;

    refreshPromise = (async () => {
        try {
            const response = await authApi.refresh(authStore.refreshToken);

            // Refresh the token before the access token expires. This means the user will stay
            // logged in without any notable hiccups or delays
            clearTimeout(refreshTimeout);

            const expiresIn = response.data.data.attributes.expires_in;
            // Schedule next automatic refresh
            refreshTimeout = setTimeout(() => refresh(), computeRefreshDelay(expiresIn));

            const accessToken = response.data.data.attributes.access_token;

            // Add the header to the API handler for every request
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

            if (response.data.data.attributes.refresh_token) {
                authStore.refreshToken = response.data.data.attributes.refresh_token;
            }

            authStore.accessToken = accessToken;
            authStore.accessTokenExpiry = Date.now() + response.data.data.attributes.expires_in * 1000;

            return `Bearer ${accessToken}`;
        } catch (error) {
            await logout({ navigate, reason: LogoutReason.SESSION_EXPIRED });
            throw error;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
            resumeQueue();
        }
    })();

    return refreshPromise;
}

/**
 * Logs out the user and cleans up authentication state
 *
 * @param {object} [optionsRaw] - Logout options
 * @param {boolean} [optionsRaw.navigate=true] - Whether to navigate to login page
 * @param {string} [optionsRaw.reason=LogoutReason.SIGN_OUT] - Reason for logout
 * @returns {Promise<void>}
 */
export async function logout(optionsRaw) {
    const authStore = useAuthStore();
    resumeQueue();

    clearTimeout(refreshTimeout);
    firstRefresh = true; // Reset so the initial-load refresh guard works after re-login

    delete api.defaults.headers.common['Authorization'];

    const defaultOptions = {
        navigate: true,
        reason: LogoutReason.SIGN_OUT,
    };

    const options = { ...defaultOptions, ...optionsRaw };

    // Revoke the server-side session on manual sign-out and session expiry
    if ([LogoutReason.SIGN_OUT, LogoutReason.SESSION_EXPIRED].includes(options.reason)) {
        try {
            await api.post('/general/logout');
        } catch {
            // Token may already be revoked, errors don't matter
        }
    }

    authStore.accessToken = null;
    authStore.refreshToken = null;
    authStore.accessTokenExpiry = 0;

    /*
     * Important to clear all the stores before navigating, otherwise route guards
     * and components on the next page can observe stale auth/user state.
     */
    await dehydrate();

    // Cancel in-flight requests so resolved results don't write back to cache after logout
    await queryClient.cancelQueries();

    // Clear all caches after stores are dehydrated
    queryClient.clear();

    if (options.navigate === true) {
        const failure = await router.replace({ name: 'login' });

        // Force redirect to login page if the navigation was aborted
        if (isNavigationFailure(failure)) {
            window.location.href = '/login';
        }
    }

    // Reset all query observers after navigation so components have unmounted
    await queryClient.resetQueries();
}

export default {
    authenticate,
    refresh,
    logout,
};
